<!--
  THE TOP BAR (Mark, 2026-09-27).

  Three changes, and the reasoning for each matters more than the diff.

  1. IT IS THE CHASSIS BLUE NOW, not white. `#header` sat on --lw-bg-paper
     beside a sidebar on --lw-bg-chassis, so the shell had a light L-shape over
     a dark rail and read as two unrelated pieces of furniture. The colour lives
     in ui/src/sass/app.scss with the other four shell ids, which is where the
     frame is painted as one system.

  2. THE HAMBURGER IS GONE FROM THE DESKTOP BAR. It was the only thing in here,
     and collapsing the sidebar is a thing you do TO the sidebar, so the control
     moved into it (sidebar.vue's rail toggle).

  3. IT KEEPS A MENU BUTTON ON NARROW SCREENS, and this is the one place I am
     deliberately not doing what was asked. Under 768px the sidebar is not a
     rail that narrows, it is a slide-in layer: app.scss gives
     `#sidebar.collapsed` `transform: translateX(-100%)` AND
     `visibility: hidden`. A control inside a hidden sidebar cannot be clicked
     or focused, so removing this button would leave a phone with no way to
     reach the navigation at all. It renders only when the viewport is narrow,
     so the desktop bar is exactly as empty as asked.

  The bell is right-aligned, which is what `justify-content: space-between` plus
  an always-present left slot gets: the menu button when there is one, and an
  empty flex item when there is not, so the bell does not jump position when a
  window crosses 768px.

  THE DEMO LINE LIVES IN THIS ROW (Mark, 2026-10-04). On demo.ladeway.com the
  "Demo fleet" sentence and the "Get your own fleet on the map" link used to be
  a separate black bar (demoBanner.vue) stacked ABOVE this one. That made the
  top of the content column 3rem plus a bar, beside a 3rem #app-title band in
  the sidebar, so the two road markings no longer met and this row sat mostly
  empty under it. Both now sit inside this row: the sentence on the left, the
  link on the right before the bell, and the row stays exactly 3rem. On a phone
  the sentence drops to "Demo fleet." so the row never wraps taller.

  `v-if`, not a class toggle: on every other hostname there must be no element,
  no reserved space and nothing in the accessibility tree, so the production
  bar renders exactly as it did before the demo existed.
-->
<template>
  <div id="header-container">
    <div class="lw-p-header__left">
      <!--
        audit_web.md 3.4 named this control twice (the href="#" idiom, and
        "focusable but unnamed"). It has been a real <button> with a real
        aria-expanded/aria-controls pair since that was fixed; all that changed
        here is that it is narrow-screen only.
      -->
      <button
        v-if="narrow"
        id="menu-collapse"
        type="button"
        :aria-expanded="String(!menuCollapsed)"
        aria-controls="sidebar"
        :aria-label="label"
        @click="menuButton()"
      >
        <i class="fa fa-bars" aria-hidden="true" />
      </button>
      <!--
        Spec 53 4.7: a practice fleet (a training lease, the store's answer,
        on any hostname) says what it is in one line, and carries none of the
        demo's sales chrome: no demo line, no walkthrough menu, no sign-up link.
        A training video leaves the line out (Mark, 2026-10-25): render mode,
        training/render.js, development builds only.
      -->
      <p v-if="trainingLease && !filming" class="lw-p-header__demo" data-test="practice-line">
        Practice fleet<span class="lw-p-header__demo-more"
          >: sample data, yours alone. Nothing here touches your company's account.</span
        >
      </p>
      <p v-else-if="isDemo && !trainingLease" class="lw-p-header__demo">
        Demo fleet.<span class="lw-p-header__demo-more">
          Sample data, and yours alone: change anything you like.</span
        >
      </p>
    </div>

    <div class="lw-p-header__right">
      <!--
        Spec 33 7.2: the walkthrough menu. Gated on the STORE's demo flag
        (GET /organization's `demo` object, the same server answer TourHost
        starts on), not on this file's hostname check: the two can disagree
        (a local demo-mode stack), and the menu must exist exactly where the
        walkthroughs can run.
      -->
      <LwTourMenu v-if="walkthroughsOn" />
      <!--
        Spec 53 8.1: Training, inside a practice fleet only, where it is the
        way back to the list. On production it is gone from the header (15.4,
        Mark 2026-11-02): it lives under your name in the rail (navUser.vue)
        and on your own user page. Never on the anonymous demo.
      -->
      <router-link
        v-if="trainingOn"
        class="lw-p-header__training"
        data-test="header-training"
        :to="{ name: 'Training' }"
        >Training</router-link
      >
      <a v-if="isDemo && !trainingLease" class="lw-p-header__demo-cta" href="https://ladeway.com"
        >Get your own fleet<span class="lw-p-header__demo-cta-more"> on the map</span></a
      >
      <notice-bell />
      <alert-bell />
    </div>
  </div>
</template>

<script>
import alertBell from './alertBell.vue';
import noticeBell from './noticeBell.vue';
import LwTourMenu from '../lw/LwTourMenu.vue';
import { renderer } from '../../training/render';

/**
 * The demo instance is presentation, not a build. The frontend image is the
 * same static bundle on production and on the demo box (literally the same
 * files, deployed twice), so the gate cannot be a build flag without splitting
 * the artifact in two. The hostname is the one thing that already differs, it
 * is knowable at runtime, and whatever is served at demo.ladeway.com IS the
 * demo. Exact match, never a substring: demo.ladeway.com.attacker.example must
 * not get to claim it.
 */
const DEMO_HOSTNAME = 'demo.ladeway.com';

export default {
  name: 'HeaderComponent',
  components: { alertBell, noticeBell, LwTourMenu },
  props: {
    // Lives in app.vue (it also drives the sidebar's own collapsed/expanded
    // class), passed down so this button's aria-expanded and its own name
    // can stay honest about the state it is about to change.
    menuCollapsed: {
      type: Boolean,
      default: false,
    },
    // Also app.vue's, from the same matchMedia it already watches. Passed down
    // rather than re-queried here: two components asking the browser the same
    // question is two answers that can disagree mid-resize.
    narrow: {
      type: Boolean,
      default: false,
    },
    // So a test can state the hostname it wants without deleting
    // `window.location` out from under jsdom. Null means "ask the browser",
    // and the browser is asked in the computed rather than in a prop default:
    // app.test.js auto-stubs this component (which keeps its prop defaults)
    // in suites that have replaced window.location with a bare setter.
    hostname: {
      type: String,
      default: null,
    },
  },
  emits: ['menuButton'],
  computed: {
    /** A training video is being filmed (training/render.js). */
    filming() {
      return Boolean(renderer());
    },
    // Spec 33 7.2: the walkthrough menu's gate, the server's answer.
    walkthroughsOn() {
      const getters = (this.$store && this.$store.getters) || {};
      return Boolean(getters.isDemo) && !this.trainingLease;
    },
    trainingLease() {
      const getters = (this.$store && this.$store.getters) || {};
      return Boolean(getters.isDemo && getters.trainingLease);
    },
    // Spec 53 15.4: the practice fleet's header only.
    trainingOn() {
      return this.trainingLease;
    },
    isDemo() {
      const host =
        this.hostname !== null
          ? this.hostname
          : typeof window !== 'undefined' && window.location
            ? window.location.hostname
            : '';
      return host === DEMO_HOSTNAME;
    },
    // "Toggle sidebar" would be accurate and still fail audit_web.md 3.4's
    // complaint: a name that only describes the mechanism makes a user guess
    // which direction they are about to get. Naming the actual, current effect
    // is honest about direction the way the brief asks.
    label() {
      return this.menuCollapsed ? 'Open navigation' : 'Close navigation';
    },
  },
  methods: {
    menuButton() {
      this.$emit('menuButton');
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

#header-container {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
}

// Always rendered, even with nothing in it: it is what holds the bell against
// the right edge, and an empty flex item keeps the bell from shifting when a
// window crosses the narrow breakpoint.
.lw-p-header__left {
  display: flex;
  align-items: center;
  height: 100%;
  // Lets the demo sentence shrink (and ellipsize) instead of pushing the bell
  // off the row. Inert when the slot is empty or holds only the menu button.
  min-width: 0;
}

.lw-p-header__right {
  display: flex;
  align-items: center;
  height: 100%;
  flex: none;
}

// The demo line, demo.ladeway.com only. Chassis ink at caption size, so it
// reads as a label on the frame and not as a page notice. --lw-fg-on-chassis,
// not --lw-fg-inverse: the chassis is dark in BOTH themes, and the inverse ink
// turns dark in the dark theme.
.lw-p-header__demo {
  margin: 0;
  padding: 0 var(--lw-space-4);
  color: var(--lw-fg-on-chassis);
  font: var(--lw-type-caption-strong);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lw-p-header__demo-more {
  color: var(--lw-fg-on-chassis-muted);
  font: var(--lw-type-caption);
}

// The amber the old bar used for this link, on the same chassis navy family.
.lw-p-header__demo-cta {
  color: var(--lw-amber-500);
  font: var(--lw-type-caption-strong);
  text-decoration: none;
  white-space: nowrap;
  padding: 0 var(--lw-space-3);

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  @include lw-focus-visible;
}

// Training: a quiet link on the frame, chassis ink like the demo line, so it
// sits beside the bells without competing with a page's own primary action.
.lw-p-header__training {
  color: var(--lw-fg-on-chassis);
  font: var(--lw-type-caption-strong);
  text-decoration: none;
  white-space: nowrap;
  padding: var(--lw-space-1) var(--lw-space-3);
  margin-right: var(--lw-space-2);
  border: 1px solid var(--lw-line-chassis);
  border-radius: var(--lw-radius-sm);

  &:hover {
    border-color: var(--lw-fg-on-chassis-muted);
  }

  &.router-link-active {
    border-color: var(--lw-amber-500);
  }

  @include lw-focus-visible;
}

// A phone keeps "Demo fleet." and the link and drops the rest of the sentence,
// so the row stays one line at 3rem rather than wrapping taller. The menu
// button already sits left of the label there, so it needs less inset.
@media (max-width: 768px) {
  .lw-p-header__demo {
    padding: 0 var(--lw-space-2);
  }

  .lw-p-header__demo-more {
    display: none;
  }

  .lw-p-header__demo-cta {
    padding: 0 var(--lw-space-2);
  }
}

// A 375px phone cannot fit the menu button, "Demo fleet.", the whole link and
// the bell on one line (it ellipsized "Demo fleet." to "Demo fl…"), so the
// link drops its last three words there. The target and the start of the
// sentence are unchanged.
@media (max-width: 480px) {
  .lw-p-header__demo-cta-more {
    display: none;
  }
}

#menu-collapse {
  // Reset browser <button> chrome.
  border: 0;
  background: transparent;
  // The bar is the chassis blue now, so the ink is the inverse ramp and the
  // hover is the same fill a hovered nav item takes (navLink.vue) rather than
  // the neutral --lw-bg-inset this button used on a white bar.
  color: var(--lw-fg-inverse);
  cursor: pointer;
  font: inherit;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 100%;
  padding: 0;

  &:hover {
    background-color: var(--lw-navy-950);
  }

  @include lw-focus-visible;
}
</style>
